/**
 * Birth-date helpers for the subscription form.
 *
 * Pure, UI-agnostic functions — `validateBirthDate` returns a stable error code
 * and the caller maps it to a localized message (i18n stays in the UI layer),
 * the same contract as `validateSsn` / `validateIban`.
 *
 * WHY THIS MODULE EXISTS
 * The decision tree asks the age question in brackets ("under 18", "18 to 30",
 * "over 30"); the form then collects the exact day/month/year. Two guards used to
 * restate the same bounds independently — the year `<select>` range and the
 * bracket check — and they had drifted apart: the year list was computed in whole
 * calendar years, so it dropped a year of legitimate subscribers at the upper
 * bound and made the "too old" case unreachable, while any day/month that pushed
 * the real age outside the bracket surfaced one vague catch-all message.
 * Deriving both from a single `AgeBounds` here is what keeps them aligned.
 */

/** Age limits a journey accepts, resolved from the product and the audience
 *  (self / child) — see `useSubscriptionOffer.ageBounds`. Both are inclusive. */
export type AgeBounds = { min: number; max: number }

/** The bracket picked at the decision tree's "age" step, when that step exists. */
export type AgeBucket = 'young' | 'mid' | 'senior'

/** The three `<select>` values of the birth-date row; `null` while unset. */
export type BirthDateParts = { day: number | null; month: number | null; year: number | null }

/**
 * Why a birth date is rejected, or `null` when it is accepted.
 *
 * `minAge` / `maxAge` are the product's own age limits and carry the bound in
 * their message; `bucket` is the residual "you don't belong in the bracket you
 * picked" mismatch, which is not an age-limit breach and keeps a generic wording.
 */
export type BirthDateError = 'required' | 'invalid' | 'future' | 'minAge' | 'maxAge' | 'bucket'

/**
 * How far back the year `<select>` goes when no bracket constrains it. Arbitrary
 * but generous: it only has to cover any plausible subscriber, since the real
 * filtering is done by the bounds above.
 */
const OLDEST_SELECTABLE_YEARS = 100

/** Whether day/month/year denote a real calendar date (rejects 31/02, 29/02 on a
 *  common year…). `new Date` would silently roll those over to the next month. */
export function isRealDate(day: number, month: number, year: number): boolean {
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

/** Completed years between `birth` and `on`, i.e. the age as everyday usage means
 *  it: the birthday must have been reached, not merely the calendar year. */
export function ageOn(birth: Date, on: Date): number {
  let age = on.getFullYear() - birth.getFullYear()
  const monthDiff = on.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && on.getDate() < birth.getDate())) age--
  return age
}

/**
 * Birth years to offer in the year `<select>`, given the bracket and bounds.
 *
 * The range is deliberately *permissive*: it keeps every year that can hold an
 * acceptable age, and leaves it to `validateBirthDate` to reject the day/month
 * combinations inside those years that fall outside the bounds. Narrowing it any
 * further would silently hide legitimate dates — someone born in December who has
 * not had their birthday yet is a year "younger" than the calendar suggests, and
 * that is exactly the case the old whole-year arithmetic excluded.
 */
export function birthYearRange(
  bounds: AgeBounds,
  bucket: string | undefined,
  today: Date,
): { min: number; max: number } {
  const year = today.getFullYear()
  // Turning `bounds.min` this year means being born in `year - bounds.min`; being
  // `bounds.max` and not yet having had this year's birthday means being born in
  // `year - bounds.max - 1`. Those two are the extremes of the accepted window.
  //
  // Both ends are therefore *partial* years, which reads as an off-by-one until
  // worked through. For 18-30 on 9 September 2026 the list runs 1995-2008:
  //   - born 01/01/2008 → 18, accepted;   born 01/12/2008 → 17, rejected;
  //   - born 31/12/1995 → 30, accepted;   born 01/01/1995 → 31, rejected.
  // Dropping 1995 because "2026 - 1995 = 31" would lock out every 30-year-old
  // born after 9 September 1995 — they could not enter their date at all.
  if (bucket === 'mid') return { min: year - bounds.max - 1, max: year - bounds.min }
  // Over the limit: the youngest such subscriber turned `bounds.max + 1` today.
  if (bucket === 'senior') return { min: year - OLDEST_SELECTABLE_YEARS, max: year - bounds.max - 1 }
  // No age step in this journey (or the "young" bracket, which the exit screen
  // stops before the form): nothing to constrain.
  return { min: year - OLDEST_SELECTABLE_YEARS, max: year }
}

/**
 * Validate the birth date entered in the form against the journey's age rules.
 *
 * `today` is the reference date on purpose: the rules are expressed "at the date
 * of subscription", not at the contract's start date, so a subscriber who reaches
 * the minimum age between signing and the cover starting is still turned away.
 */
export function validateBirthDate(
  parts: BirthDateParts,
  options: { bounds: AgeBounds; bucket: string | undefined; today: Date },
): BirthDateError | null {
  const { day, month, year } = parts
  // `0` is not a valid day/month/year either, so a plain falsy test is enough.
  if (!day || !month || !year) return 'required'
  if (!isRealDate(day, month, year)) return 'invalid'

  const birth = new Date(year, month - 1, day)
  if (birth > options.today) return 'future'

  const age = ageOn(birth, options.today)
  const { min, max } = options.bounds

  if (options.bucket === 'mid') {
    if (age < min) return 'minAge'
    if (age > max) return 'maxAge'
    return null
  }
  // The "over the limit" bracket is a dead end for most products, but a few
  // (renforcer-sante, solidarite-exam for students) let it through. Landing here
  // with an age within the limits means the bracket itself was mis-picked.
  if (options.bucket === 'senior') return age > max ? null : 'bucket'

  return null
}
